import React, { useEffect, useState, useMemo } from 'react';
import Iframe from 'react-iframe';
import { Box } from '@mui/material';
import { isNumeric } from '../util/TypeScriptUtil';
import '../css/views/externalLanding.css';

interface ExternalLandingProps {
  url: string;
  height?: string;
}

const IFRAME_RESIZE_KEY = 'securspace-iframe-resize';
const IFRAME_HEIGHT_PADDING = 0;  //Note: this was meant to allow for padding, but it appears to trigger a
                                  //feedback loop that can cause some iframed pages to grow infinitely.
                                  //Keeping it here in case we want to add padding back in the future, but setting
                                  //it to 0 for now to avoid the issue.

const calculatePaddedIframeHeight: (contentHeight: number | string) => string = (contentHeight: number | string) => {
  if (!isNumeric(String(contentHeight))) {
    return String(contentHeight);
  }
  const totalHeight = Number(contentHeight) + IFRAME_HEIGHT_PADDING;
  return `${totalHeight}px`;
}

const ExternalLanding = ({
  url,
  height = '100%',
}: ExternalLandingProps) => {
  const [iframeHeight, setIframeHeight] = useState<string>(calculatePaddedIframeHeight(height || '100%'));

  const expectedOrigin = useMemo(() => {
    try {
      return new URL(url).origin;
    } catch {
      return undefined;
    }
  }, [url]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (event?.data?.height && (String(event?.data?.key) === IFRAME_RESIZE_KEY)) {
        setIframeHeight(calculatePaddedIframeHeight(event.data.height));
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [expectedOrigin]);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100dvh - var(--ss-nav-height, 64px))',
      }}
    >
      <Iframe
        url={url}
        width="100%"
        height={iframeHeight}
        scrolling="no"
        frameBorder={0}
      />
    </Box>
  );
};

export default ExternalLanding;